import { useEffect, useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { adminApi, fmtTRY, fmtDate } from "@/lib/api";
import { Search, BookOpen, Plus } from "lucide-react";

export default function ProductMemoryView({ PageHeader }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searchMessage, setSearchMessage] = useState("");
  const searchRequest = useRef(0);
  const [selected, setSelected] = useState(null);
  const [memory, setMemory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [form, setForm] = useState({ supplier_name:"", list_price:"", discount:"", source:"", date:"", extra_feature:"", note:"" });

  useEffect(() => {
    const requestId = ++searchRequest.current;
    setSearchMessage("");
    const t = setTimeout(async () => {
      if (!q.trim()) { setResults([]); return; }
      try {
        const r = await adminApi.get("/products/search", { params: { q, limit: 30 } });
        if (requestId !== searchRequest.current) return;
        const data = r.data;
        setResults(Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
        setSearchMessage(Array.isArray(data) ? "" : String(data?.message || ""));
      } catch {
        if (requestId !== searchRequest.current) return;
        setResults([]);
        setSearchMessage("Katalog araması tamamlanamadı. Yeniden deneyin.");
      }
    }, 250);
    return () => { clearTimeout(t); searchRequest.current += 1; };
  }, [q]);

  const loadMemory = async (p) => {
    setSelected(p); setLoading(true);
    try {
      const r = await adminApi.get(`/admin/products/${p.id}/memory`);
      setMemory(r.data); setNote(r.data?.internal_note || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Ürün hafızası alınamadı."); }
    finally { setLoading(false); }
  };

  const saveNote = async () => {
    if (!selected) return;
    await adminApi.put(`/admin/products/${selected.id}/internal-note`, { note });
    toast.success("İç not kaydedildi.");
    loadMemory(selected);
  };

  const addHistory = async () => {
    if (!selected || !form.supplier_name.trim()) return toast.error("Firma adı girin.");
    try {
      await adminApi.post(`/admin/products/${selected.id}/memory`, {
        supplier_name: form.supplier_name,
        list_price: form.list_price === "" ? null : Number(String(form.list_price).replace(",", ".")),
        discount: form.discount === "" ? null : Number(String(form.discount).replace(",", ".")),
        extra_feature: form.extra_feature,
        note: form.note,
      });
      toast.success("Tedarikçi fiyatı hafızaya eklendi.");
      setForm({ supplier_name:"", list_price:"", discount:"", source:"", date:"", extra_feature:"", note:"" });
      loadMemory(selected);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt eklenemedi."); }
  };

  return (
    <div>
      <PageHeader title="Tedarikçi Hafızası" subtitle="Ürün bazında geçmiş firma, liste fiyatı, iskonto ve iç notları görüntüleyin" />
      <div className="p-8 grid grid-cols-1 xl:grid-cols-3 gap-5">
        <Card className="border-[#E2E8F0] p-4 xl:col-span-1 h-fit">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Ürün adı veya kod ara…" className="pl-9" />
          </div>
          <div className="mt-3 max-h-[65vh] overflow-y-auto divide-y">
            {searchMessage && results.length === 0 && <p role="status" className="py-4 text-sm text-[#64748B]">{searchMessage}</p>}
            {results.map((p) => (
              <button key={p.id} onClick={()=>loadMemory(p)} className={`w-full text-left py-3 px-2 hover:bg-[#F8FAFC] ${selected?.id===p.id ? "bg-orange-50" : ""}`}>
                <div className="text-sm font-medium text-[#1F2937]">{p.name}</div>
                <div className="text-xs text-[#6B7280] mt-0.5">{p.brand_name} {p.code ? `· ${p.code}` : ""} · {Number(p.list_price||0)>0 ? fmtTRY(p.list_price) : "Fiyat yok"}</div>
              </button>
            ))}
          </div>
        </Card>

        <div className="xl:col-span-2 space-y-5">
          {!selected ? (
            <Card className="border-[#E2E8F0] p-12 text-center text-[#6B7280]"><BookOpen className="w-9 h-9 mx-auto mb-3" />Soldan bir ürün seçin.</Card>
          ) : loading ? <div>Yükleniyor…</div> : memory && (
            <>
              <Card className="border-[#E2E8F0] p-5">
                <h3 className="font-semibold text-[#1E3A5F]">{memory.product?.name}</h3>
                <p className="text-xs text-[#6B7280] mt-1">{memory.brand?.name} · {memory.product?.code || "Kod yok"}</p>
                <div className="mt-4 space-y-2">
                  <Label>Kalıcı İç Not <span className="text-[#9CA3AF]">(müşteri görmez)</span></Label>
                  <Textarea value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Ürünle ilgili özel bilgi, hangi firmadan alınır, dikkat edilmesi gereken özellik…" />
                  <div className="text-right"><Button onClick={saveNote} size="sm">Notu Kaydet</Button></div>
                </div>
              </Card>

              <Card className="border-[#E2E8F0] p-5">
                <h4 className="font-semibold text-sm text-[#1E3A5F] mb-3">Yeni Tedarikçi Fiyatı Ekle</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <F label="Firma"><Input value={form.supplier_name} onChange={(e)=>setForm({...form,supplier_name:e.target.value})} /></F>
                  <F label="Kaynak"><Input value={form.source} onChange={(e)=>setForm({...form,source:e.target.value})} placeholder="Telefon / WhatsApp / PDF / E-posta" /></F>
                  <F label="Fiyat Tarihi"><Input type="date" value={form.date} onChange={(e)=>setForm({...form,date:e.target.value})} /></F>
                  <F label="Liste Fiyatı"><Input value={form.list_price} onChange={(e)=>setForm({...form,list_price:e.target.value})} /></F>
                  <F label="İskonto %"><Input value={form.discount} onChange={(e)=>setForm({...form,discount:e.target.value})} /></F>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <F label="Ekstra özellik"><Input value={form.extra_feature} onChange={(e)=>setForm({...form,extra_feature:e.target.value})} /></F>
                  <F label="Not"><Input value={form.note} onChange={(e)=>setForm({...form,note:e.target.value})} /></F>
                </div>
                <div className="text-right mt-3"><Button onClick={addHistory} className="bg-[#F97316] hover:bg-[#EA580C] text-white"><Plus className="w-4 h-4 mr-2"/>Kaydet</Button></div>
              </Card>

              <Card className="border-[#E2E8F0] overflow-hidden">
                <div className="px-5 py-4 border-b font-semibold text-sm text-[#1E3A5F]">Geçmiş Fiyatlar</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-[#F8FAFC]"><Th>Tarih</Th><Th>Firma</Th><Th>Kaynak</Th><Th>Özellik</Th><Th className="text-right">Liste</Th><Th className="text-right">İsk.</Th><Th className="text-right">Net</Th><Th>Not</Th></tr></thead>
                    <tbody>
                      {(memory.history || []).length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-[#9CA3AF]">Henüz kayıt yok.</td></tr> : memory.history.map((h)=>(
                        <tr key={h.id} className="border-b">
                          <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDate(h.price_date || h.created_at)}</td>
                          <td className="px-4 py-3 font-medium">{h.supplier_name || "—"}</td><td className="px-4 py-3">{h.source || "—"}</td>
                          <td className="px-4 py-3 text-xs">{h.extra_feature || "—"}</td>
                          <td className="px-4 py-3 text-right">{fmtTRY(h.list_price)}</td>
                          <td className="px-4 py-3 text-right">%{Number(h.discount||0).toFixed(1)}</td>
                          <td className="px-4 py-3 text-right font-medium text-[#F97316]">{fmtTRY(h.net_price)}</td>
                          <td className="px-4 py-3 text-xs">{h.note || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function F({label,children}) { return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>; }
const Th=({children,className=""})=><th className={`px-4 py-3 text-[10px] uppercase tracking-[0.1em] font-semibold border-b border-[#E2E8F0] text-left text-[#4B5563] ${className}`}>{children}</th>;
